// src/components/digital_twin/twinIntel.js
// --- UPDATE (Digital Twin MOW+ v1) — NEW FILE -----------------------------------------------
// FlowSense AI/ML for the digital twin. Pure functions (no React), so every screen and the tests
// use the same logic:
//
//   • createTelemetryModel()   streaming ML per sensor — exponentially weighted mean / variance
//                              (EWMA), z-score anomaly detection (spike / drop), stuck-sensor
//                              (flat-line) detection, rate of change, rolling history buffer.
//   • liveSeverity()           the same sensor status rules the twin page uses for colouring
//                              (engine state → status → water level / thresholds).
//   • matchPsiCondition()      the configured PSI condition band for a value.
//   • detectSuspectedLeaks()   pipelines whose linked pressure sensors drop together (leak / burst).
//   • assetRisk()              one explainable risk score per asset from the ML health model
//                              (failure probability, RUL), live status, alerts, issues and
//                              anomalies on its sensors — every point of the score has a driver.
//   • summarizeNetwork()       network health, reporting / stale sensors, counts.
//   • buildTwinFindings()      what needs attention, with an action (fly to / open sensor).
//   • explain… / answerTwinLocally() / buildTwinContext()   the AI explainer (rules when the
//                              FlowSense AI engine is offline, compact context when it is online).
//
// Rule: never invent numbers. Anything we do not have is null and shown as "—".
// --- END UPDATE ---------------------------------------------------------------------------

export const SEVERITY = Object.freeze({ CRITICAL: 'critical', WARNING: 'warning', NORMAL: 'normal', UNKNOWN: 'unknown' });

export const TWIN_AI_DEFAULTS = Object.freeze({
    alpha: 0.12,            // EWMA smoothing — about the last 8 readings carry most weight
    zWarn: 3,               // |z| ≥ 3 → warning anomaly
    zCrit: 4.5,             // |z| ≥ 4.5 → critical anomaly
    minSamples: 8,          // readings needed before a sensor is scored
    flatSamples: 20,        // identical readings in a row → possibly stuck sensor
    historyMax: 240,        // points kept per sensor (sparklines, drawer chart)
    dropPct: 25,            // pressure ≥ 25 % below its normal level → leak signal
    staleMs: 15 * 60 * 1000, // no reading for 15 min → stale
    anomalyHoldMs: 10 * 60 * 1000, // an anomaly stays on the list for 10 min
    defaultMaxPsi: 90,      // same defaults the twin page uses when a sensor has no thresholds
    defaultMinPsi: 20,
});

// ─── basic helpers ───────────────────────────────────────────────────────────
export const num = (v) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
export const sensorKey = (s) => String(s?.device_id ?? s?.deviceId ?? s?.id ?? '');
export const isWaterLevelSensor = (s) => String(s?.type || '').toLowerCase() === 'water_level';
const LEVEL_WORDS = Object.freeze({ EMPTY: 0, LOW: 25, NORMAL: 50, MEDIUM: 50, OK: 50, HIGH: 85, FULL: 100, CRITICAL: 100, OVERFLOW: 100 });
export const d = (v, digits = null) => {
    const n = num(v);
    if (n === null) return '—';
    return digits === null ? String(Math.round(n * 100) / 100) : n.toFixed(digits);
};
export const pct = (p) => (num(p) === null ? '—' : `${Math.round(num(p) * 100)}%`);

/** Numeric value of a reading ({psi}, {pressure}, {value}, {level}, a number or "HIGH"). */
export function readingValue(r) {
    if (r === null || r === undefined) return null;
    if (typeof r === 'number') return Number.isFinite(r) ? r : null;
    if (typeof r === 'string') { const n = num(r); return n !== null ? n : (LEVEL_WORDS[r.trim().toUpperCase()] ?? null); }
    if (typeof r === 'object') {
        for (const k of ['psi', 'pressure', 'value', 'level', 'flow_rate']) {
            if (r[k] !== undefined && r[k] !== null) { const v = readingValue(r[k]); if (v !== null) return v; }
        }
    }
    return null;
}
/** The word form of a water level reading ("HIGH"), when the sensor reports words. */
export function readingWord(r) {
    const raw = r && typeof r === 'object' ? (r.level ?? r.value) : r;
    return typeof raw === 'string' && num(raw) === null ? raw.trim().toUpperCase() : null;
}
export function readingTime(r) {
    if (!r || typeof r !== 'object') return null;
    const t = r.timestamp ?? r.received_at ?? r.ts ?? r.time;
    if (t === null || t === undefined) return null;
    const ms = typeof t === 'number' ? t : Date.parse(t);
    return Number.isFinite(ms) ? ms : null;
}
export function unitOf(sensor) {
    const t = String(sensor?.type || 'pressure').toLowerCase();
    if (t === 'water_level') return '';
    if (t === 'flow_rate') return 'GPM';
    if (t === 'temperature') return '°C';
    if (t === 'battery_level') return '%';
    return 'PSI';
}
export function formatReading(sensor, reading) {
    const w = readingWord(reading);
    if (w) return w;
    const v = readingValue(reading);
    if (v === null) return '—';
    const u = unitOf(sensor);
    return `${Math.round(v * 100) / 100}${u ? ` ${u}` : ''}`;
}
export const sensorName = (s) => s?.name || `Sensor ${sensorKey(s)}`;
export const assetName = (a) => a?.name || `${a?.asset_type || a?.type || 'Asset'} ${a?.id ?? ''}`.trim();
export function coords(o) {
    const la = num(o?.latitude ?? o?.lat), lo = num(o?.longitude ?? o?.lng ?? o?.lon);
    if (la === null || lo === null || (la === 0 && lo === 0) || Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
    return { lat: la, lng: lo };
}

// ─── status rules ────────────────────────────────────────────────────────────
const mapWord = (w) => {
    const s = String(w || '').toLowerCase();
    if (!s || s === 'ok' || s === 'normal' || s === 'good') return SEVERITY.NORMAL;
    if (['critical', 'alarm', 'danger', 'burst', 'high_alarm', 'low_alarm', 'error', 'fault'].includes(s)) return SEVERITY.CRITICAL;
    return SEVERITY.WARNING;
};

/**
 * Live severity of one sensor reading — mirrors the rules in DigitalTwinPage (entityLiveStatus),
 * so the AI panel and the 3D colours never disagree. Returns { severity, reason, source }.
 */
export function liveSeverity(sensor, reading, defaults = TWIN_AI_DEFAULTS) {
    if (!reading) return { severity: SEVERITY.UNKNOWN, reason: 'No reading', source: null };
    if (reading.state && reading.state !== 'ok') return { severity: mapWord(reading.state), reason: `Twin engine state "${reading.state}"`, source: 'engine' };
    if (reading.status && reading.status !== 'normal') return { severity: mapWord(reading.status), reason: `Reported status "${reading.status}"`, source: 'device' };
    const val = num(reading.value !== undefined ? reading.value : reading.psi);
    if (isWaterLevelSensor(sensor)) {
        const w = readingWord(reading);
        const lv = val !== null ? val : readingValue(reading);
        if (w === 'HIGH' || w === 'EMPTY' || (lv !== null && (lv > 85 || lv < 10))) return { severity: SEVERITY.CRITICAL, reason: w ? `Level ${w}` : `Level ${d(lv)} outside 10–85`, source: 'level_rule' };
        return { severity: SEVERITY.NORMAL, reason: w ? `Level ${w}` : 'Level within 10–85', source: 'level_rule' };
    }
    if (val === null) return { severity: SEVERITY.UNKNOWN, reason: 'Reading has no numeric value', source: null };
    const maxT = num(sensor?.max_threshold) ?? defaults.defaultMaxPsi;
    const minT = num(sensor?.min_threshold) ?? defaults.defaultMinPsi;
    const own = num(sensor?.max_threshold) !== null || num(sensor?.min_threshold) !== null;
    const src = own ? 'sensor_threshold' : 'default_threshold';
    if (val > maxT) return { severity: SEVERITY.CRITICAL, reason: `${d(val)} PSI above the ${d(maxT)} PSI limit`, source: src };
    if (val < minT) return { severity: SEVERITY.CRITICAL, reason: `${d(val)} PSI below the ${d(minT)} PSI minimum`, source: src };
    if (val > maxT * 0.8) return { severity: SEVERITY.WARNING, reason: `${d(val)} PSI above 80 % of the ${d(maxT)} PSI limit`, source: src };
    return { severity: SEVERITY.NORMAL, reason: `${d(val)} PSI within ${d(minT)}–${d(maxT)} PSI`, source: src };
}

/** The configured PSI condition (status_label, color_code) that contains this value. */
export function matchPsiCondition(value, psiConditions = []) {
    const v = num(value);
    if (v === null || !Array.isArray(psiConditions)) return null;
    return psiConditions.find((c) => num(c.min_psi) !== null && num(c.max_psi) !== null && v >= num(c.min_psi) && v <= num(c.max_psi)) || null;
}

/** Gaps and overlaps in the PSI condition table (used by PsiConditionManager). */
export function analysePsiConditions(conditions = []) {
    const rows = (conditions || []).map((c) => ({ ...c, lo: num(c.min_psi), hi: num(c.max_psi) })).filter((c) => c.lo !== null && c.hi !== null).sort((a, b) => a.lo - b.lo);
    const problems = [];
    (conditions || []).forEach((c) => {
        if (num(c.min_psi) === null || num(c.max_psi) === null) problems.push({ type: 'invalid', text: `"${c.status_label || 'unnamed'}" has no valid range.` });
        else if (num(c.min_psi) > num(c.max_psi)) problems.push({ type: 'invalid', text: `"${c.status_label}" has min ${c.min_psi} above max ${c.max_psi}.` });
    });
    for (let i = 1; i < rows.length; i += 1) {
        const a = rows[i - 1], b = rows[i];
        if (b.lo <= a.hi) problems.push({ type: 'overlap', text: `"${a.status_label}" (${a.lo}–${a.hi}) and "${b.status_label}" (${b.lo}–${b.hi}) overlap — a reading of ${b.lo} matches both.` });
        else if (b.lo - a.hi > 1) problems.push({ type: 'gap', text: `No condition covers ${a.hi}–${b.lo} PSI.` });
    }
    return { rows, problems, min: rows.length ? rows[0].lo : null, max: rows.length ? Math.max(...rows.map((r) => r.hi)) : null };
}

// ─── streaming ML per sensor ─────────────────────────────────────────────────
/**
 * Online anomaly model. update(key, value, ts) is O(1); state is kept per sensor.
 * EWMA mean/variance: diff = x − μ; μ += α·diff; σ² = (1 − α)(σ² + α·diff²).
 * While a reading is anomalous α is reduced so one burst does not become the new "normal".
 */
export function createTelemetryModel(options = {}) {
    const o = { ...TWIN_AI_DEFAULTS, ...options };
    const state = new Map();

    function update(key, value, ts = null) {
        const v = num(value);
        if (key === null || key === undefined || v === null) return null;
        const k = String(key);
        const t = num(ts) ?? Date.now();
        let s = state.get(k);
        if (!s) { s = { n: 0, mean: v, var: 0, last: null, lastTs: null, flat: 0, hist: [], out: null, lastAnomaly: null }; state.set(k, s); }
        if (s.lastTs !== null && t === s.lastTs && v === s.last) return s.out; // same reading delivered twice
        const std = Math.sqrt(s.var);
        const z = s.n >= o.minSamples && std > 1e-6 ? (v - s.mean) / std : null;
        const rate = s.last !== null && s.lastTs !== null && t > s.lastTs ? (v - s.last) / ((t - s.lastTs) / 60000) : null;
        const dropPct = s.n >= o.minSamples && s.mean > 0 ? ((s.mean - v) / s.mean) * 100 : null;
        let level = null, kind = null;
        if (z !== null && Math.abs(z) >= o.zWarn) { level = Math.abs(z) >= o.zCrit ? SEVERITY.CRITICAL : SEVERITY.WARNING; kind = z < 0 ? 'drop' : 'spike'; }
        s.flat = s.last !== null && v === s.last ? s.flat + 1 : 0;
        if (!kind && s.flat >= o.flatSamples && s.n >= o.minSamples) { kind = 'flatline'; level = 'info'; }
        const a = level && level !== 'info' ? o.alpha * 0.25 : o.alpha;
        const diff = v - s.mean; const incr = a * diff;
        s.mean += incr; s.var = (1 - a) * (s.var + diff * incr);
        s.n += 1; s.last = v; s.lastTs = t;
        s.hist.push({ t, v }); if (s.hist.length > o.historyMax) s.hist.shift();
        s.out = { key: k, value: v, z, mean: s.mean, std: Math.sqrt(s.var), rate, dropPct, kind, level, samples: s.n, at: t };
        if (level && level !== 'info') s.lastAnomaly = s.out;
        return s.out;
    }
    /** Pre-train from history (any order); skipped when the sensor already has enough samples. */
    function seed(key, points = []) {
        const k = String(key);
        const s = state.get(k);
        if (s && s.n >= o.minSamples) return 0;
        const pts = (points || []).map((p) => ({ v: readingValue(p), t: readingTime(p) ?? num(p?.t) })).filter((p) => p.v !== null).sort((x, y) => (x.t ?? 0) - (y.t ?? 0));
        pts.forEach((p) => update(k, p.v, p.t));
        return pts.length;
    }
    const get = (key) => state.get(String(key))?.out || null;
    const history = (key) => (state.get(String(key))?.hist || []).slice();
    function activeAnomaly(key, now = Date.now()) {
        const s = state.get(String(key));
        if (!s) return null;
        if (s.out && s.out.level && s.out.level !== 'info') return s.out;
        if (s.lastAnomaly && now - s.lastAnomaly.at <= o.anomalyHoldMs) return s.lastAnomaly;
        return null;
    }
    const isFlat = (key) => state.get(String(key))?.out?.kind === 'flatline';
    return { update, seed, get, history, activeAnomaly, isFlat, keys: () => [...state.keys()], size: () => state.size, options: o, reset: () => state.clear() };
}

// ─── linking helpers ─────────────────────────────────────────────────────────
/** entityId → sensors linked to it (direct link or sensor→pipeline link table). */
export function sensorsByEntity(sensors = [], links = {}) {
    const m = new Map();
    const add = (id, s) => { if (id === null || id === undefined || id === '') return; const k = String(id); if (!m.has(k)) m.set(k, []); if (!m.get(k).includes(s)) m.get(k).push(s); };
    (sensors || []).forEach((s) => { add(s.linked_entity_id, s); add(links?.[sensorKey(s)], s); if (s.pipeline_id) add(s.pipeline_id, s); });
    return m;
}

// ─── leaks / bursts ──────────────────────────────────────────────────────────
/**
 * A pipeline is a suspected leak / burst when its pressure sensors show a sharp drop:
 * an ML "drop" anomaly or a reading ≥ dropPct below the sensor's learned normal level.
 * Confidence is qualitative (never a made-up probability): high = 2+ sensors agree or one
 * sensor is a critical drop below its minimum; medium = one critical drop; low = warning only.
 */
export function detectSuspectedLeaks({ pipelines = [], sensors = [], links = {}, model, readings = {}, now = Date.now(), options = {} } = {}) {
    if (!model) return [];
    const o = { ...TWIN_AI_DEFAULTS, ...options };
    const byEntity = sensorsByEntity(sensors, links);
    const out = [];
    (pipelines || []).forEach((p) => {
        const linked = (byEntity.get(String(p.id)) || []).filter((s) => !isWaterLevelSensor(s));
        const signals = [];
        linked.forEach((s) => {
            const k = sensorKey(s);
            const cur = model.get(k);
            const an = model.activeAnomaly(k, now);
            const drop = (an && an.kind === 'drop') ? an : (cur && num(cur.dropPct) !== null && cur.dropPct >= o.dropPct ? { ...cur, level: SEVERITY.WARNING, kind: 'drop' } : null);
            if (drop) {
                const sev = liveSeverity(s, readings[k]);
                signals.push({ sensor: s, key: k, z: drop.z, dropPct: drop.dropPct, level: drop.level, value: drop.value, mean: drop.mean, belowMin: sev.severity === SEVERITY.CRITICAL && /below/.test(sev.reason) });
            }
        });
        if (!signals.length) return;
        const crit = signals.filter((x) => x.level === SEVERITY.CRITICAL);
        const confidence = signals.length >= 2 || (crit.length && crit.some((x) => x.belowMin)) ? 'high' : crit.length ? 'medium' : 'low';
        const lead = crit[0] || signals[0];
        out.push({
            pipelineId: p.id, pipeline: p, confidence, sensors: signals.map((x) => x.key), signals,
            text: `${signals.length} pressure sensor(s) on ${p.name || `pipeline ${p.id}`} dropped: ${signals.map((x) => `${x.key} ${d(x.value)} PSI vs normal ${d(x.mean)}${x.dropPct !== null ? ` (−${Math.round(x.dropPct)} %)` : ''}`).join('; ')}.`,
            lead,
        });
    });
    const rank = { high: 3, medium: 2, low: 1 };
    return out.sort((a, b) => rank[b.confidence] - rank[a.confidence]);
}

// ─── asset risk ──────────────────────────────────────────────────────────────
export function failureProbability(health) {
    const p = num(health?.failure_probability ?? health?.failure_prob ?? health?.risk_probability);
    if (p === null) return null;
    return p > 1 ? Math.min(1, p / 100) : Math.max(0, p);
}
export const RISK_BANDS = Object.freeze([
    { min: 70, band: 'critical', color: 'red', label: 'Critical' },
    { min: 40, band: 'elevated', color: 'orange', label: 'Elevated' },
    { min: 15, band: 'watch', color: 'yellow', label: 'Watch' },
    { min: 0, band: 'low', color: 'green', label: 'Low' },
]);
export const riskBand = (score) => (num(score) === null ? { band: 'unknown', color: 'gray', label: 'Not scored' } : RISK_BANDS.find((b) => score >= b.min));

/**
 * Explainable risk score 0–100. Returns { score|null, band, drivers[{label, points}], actions[] }.
 * score is null when there is no evidence at all (no ML health, no live data, no alerts/issues).
 */
export function assetRisk(asset, { health = null, live = null, alerts = [], issues = [], anomalies = [], stale = [], sim = null } = {}) {
    const drivers = []; let score = 0; let evidence = 0;
    const add = (points, label) => { if (points > 0) { score += points; drivers.push({ label, points: Math.round(points) }); } };
    const p = failureProbability(health);
    if (p !== null) { evidence += 1; add(p * 60, `ML failure probability ${pct(p)}`); }
    const hs = String(health?.health_status || '').toLowerCase();
    if (hs) evidence += 1;
    if (p === null && hs === 'critical') add(35, 'ML health status critical');
    else if (p === null && hs === 'warning') add(15, 'ML health status warning');
    const rul = num(health?.predicted_rul_days);
    if (rul !== null) { evidence += 1; if (rul <= 7) add(20, `Remaining useful life ${d(rul)} days (≤ 7)`); else if (rul <= 30) add(10, `Remaining useful life ${d(rul)} days (≤ 30)`); }
    if (live) { evidence += 1; if (live === SEVERITY.CRITICAL) add(20, 'Live sensor reading critical'); else if (live === SEVERITY.WARNING) add(10, 'Live sensor reading warning'); }
    const na = (alerts || []).length; if (na) { evidence += 1; add(Math.min(15, na * 5), `${na} active alert(s)`); }
    const ni = (issues || []).length; if (ni) { evidence += 1; add(Math.min(9, ni * 3), `${ni} reported issue(s)`); }
    const crit = (anomalies || []).filter((a) => a.level === SEVERITY.CRITICAL).length; const warn = (anomalies || []).length - crit;
    if (anomalies?.length) { evidence += 1; add(Math.min(24, crit * 12 + warn * 6), `${anomalies.length} sensor anomaly(ies) detected by FlowSense ML`); }
    if (sim) { evidence += 1; if (sim === SEVERITY.CRITICAL) add(10, 'Critical in the active simulation'); else if (sim === SEVERITY.WARNING) add(5, 'Affected in the active simulation'); }
    const final = evidence ? Math.min(100, Math.round(score)) : null;
    const actions = [];
    if (anomalies?.some((a) => a.kind === 'drop')) actions.push('Pressure dropped on a linked sensor — check for a leak or burst near this asset.');
    if (live === SEVERITY.CRITICAL) actions.push('A linked reading is outside its limits — dispatch a crew or confirm on site.');
    if (rul !== null && rul <= 7) actions.push(`Plan maintenance within ${d(rul)} days, before the predicted end of useful life.`);
    else if (rul !== null && rul <= 30) actions.push('Schedule preventive maintenance this month.');
    if (p !== null && p >= 0.5) actions.push('High ML failure probability — prioritise an inspection.');
    if (na) actions.push('Review and acknowledge the active alerts.');
    if (stale?.length) actions.push(`${stale.length} linked sensor(s) stopped reporting — check power and communications.`);
    if (!actions.length && final !== null) actions.push('No action needed — keep monitoring.');
    return { score: final, ...riskBand(final), drivers: drivers.sort((a, b) => b.points - a.points), actions, evidence };
}

// ─── network summary + findings ──────────────────────────────────────────────

/** One pass over everything the twin knows. */
export function computeTwinIntel({
    assets = [], pipelines = [], sensors = [], links = {}, readings = {}, health = {}, alerts = [], issues = [],
    model = null, isLive = true, now = Date.now(), simStates = null, floodPredictions = [], serverAnomalies = [], options = {},
} = {}) {
    const o = { ...TWIN_AI_DEFAULTS, ...options };
    const byEntity = sensorsByEntity(sensors, links);
    const sensorRows = (sensors || []).map((s) => {
        const k = sensorKey(s);
        const r = readings?.[k];
        const sev = liveSeverity(s, r, o);
        const ts = readingTime(r);
        const stale = !!(isLive && r && ts !== null && now - ts > o.staleMs);
        const anomaly = model ? model.activeAnomaly(k, now) : null;
        const ml = model ? model.get(k) : null;
        return { sensor: s, key: k, reading: r || null, hasReading: !!r, severity: sev.severity, reason: sev.reason, source: sev.source, ts, stale, anomaly, ml, flat: !!(ml && ml.kind === 'flatline') };
    });
    const sensorByKey = new Map(sensorRows.map((x) => [x.key, x]));
    const anomalies = sensorRows.filter((x) => x.anomaly).map((x) => ({ ...x.anomaly, sensor: x.sensor, key: x.key }));
    const staleRows = sensorRows.filter((x) => x.stale);
    const leaks = detectSuspectedLeaks({ pipelines, sensors, links, model, readings, now, options: o });

    const liveByEntity = new Map();
    byEntity.forEach((list, id) => {
        let worst = null;
        list.forEach((s) => { const row = sensorByKey.get(sensorKey(s)); if (!row || !row.hasReading) return; if (row.severity === SEVERITY.CRITICAL) worst = SEVERITY.CRITICAL; else if (row.severity === SEVERITY.WARNING && worst !== SEVERITY.CRITICAL) worst = SEVERITY.WARNING; else if (!worst) worst = SEVERITY.NORMAL; });
        if (worst) liveByEntity.set(id, worst);
    });

    const groupBy = (rows) => { const m = new Map(); (rows || []).forEach((x) => { const k = String(x?.asset_id ?? x?.assetId ?? x?.linked_entity_id ?? ''); if (!k) return; if (!m.has(k)) m.set(k, []); m.get(k).push(x); }); return m; };
    const alertsByAsset = groupBy(alerts); const issuesByAsset = groupBy(issues);
    const risks = (assets || []).map((a) => {
        const id = String(a.id);
        const linked = byEntity.get(id) || [];
        const an = linked.map((s) => sensorByKey.get(sensorKey(s))?.anomaly).filter(Boolean);
        const st = linked.map((s) => sensorByKey.get(sensorKey(s))).filter((x) => x && x.stale);
        const r = assetRisk(a, {
            health: health?.[id] || health?.[a.id] || null, live: liveByEntity.get(id) || null,
            alerts: alertsByAsset.get(id) || [], issues: issuesByAsset.get(id) || [],
            anomalies: an, stale: st, sim: simStates && typeof simStates.get === 'function' ? simStates.get(id) || null : null,
        });
        return { asset: a, id, name: assetName(a), coords: coords(a), sensors: linked.map(sensorKey), ...r };
    }).filter((r) => r.score !== null).sort((x, y) => y.score - x.score);
    const riskById = new Map(risks.map((r) => [r.id, r]));

    let healthCritical = 0, healthWarning = 0, scored = 0;
    Object.values(health || {}).forEach((h) => { const s = String(h?.health_status || '').toLowerCase(); if (!s) return; scored += 1; if (s === 'critical') healthCritical += 1; else if (s === 'warning') healthWarning += 1; });
    const reporting = sensorRows.filter((x) => x.hasReading).length;
    const sevCount = (sev) => sensorRows.filter((x) => x.hasReading && x.severity === sev).length;
    const openAlerts = (alerts || []).filter((a) => !['resolved', 'closed', 'acknowledged'].includes(String(a.status || a.state || '').toLowerCase())).length;

    // Network health 0–100 — every deduction is listed in `scoreDrivers` (shown in the AI panel).
    let score = null; const scoreDrivers = [];
    if ((assets || []).length || sensorRows.length) {
        score = 100;
        const ded = (pts, label) => { const p = Math.round(pts); if (p > 0) { score -= p; scoreDrivers.push({ label, points: -p }); } };
        if (scored) { ded((healthCritical / scored) * 40, `${healthCritical} of ${scored} scored assets critical (ML)`); ded((healthWarning / scored) * 15, `${healthWarning} of ${scored} scored assets warning (ML)`); }
        if (reporting) { ded((sevCount(SEVERITY.CRITICAL) / reporting) * 25, `${sevCount(SEVERITY.CRITICAL)} of ${reporting} reporting sensors critical`); ded((sevCount(SEVERITY.WARNING) / reporting) * 8, `${sevCount(SEVERITY.WARNING)} of ${reporting} reporting sensors warning`); }
        if (sensorRows.length && isLive) ded((staleRows.length / sensorRows.length) * 10, `${staleRows.length} of ${sensorRows.length} sensors stale`);
        ded(Math.min(15, leaks.length * 5), `${leaks.length} suspected leak(s)`);
        ded(Math.min(10, anomalies.length * 2), `${anomalies.length} live anomaly(ies)`);
        score = Math.max(0, Math.min(100, score));
    }

    const summary = {
        at: now, isLive,
        assets: (assets || []).length, pipelines: (pipelines || []).length, sensors: sensorRows.length,
        reporting, noData: sensorRows.length - reporting, stale: staleRows.length,
        sensorCritical: sevCount(SEVERITY.CRITICAL), sensorWarning: sevCount(SEVERITY.WARNING), sensorNormal: sevCount(SEVERITY.NORMAL),
        scored, healthCritical, healthWarning, anomalies: anomalies.length, leaks: leaks.length,
        highRisk: risks.filter((r) => r.band === 'critical').length, elevatedRisk: risks.filter((r) => r.band === 'elevated').length,
        alerts: (alerts || []).length, openAlerts, issues: (issues || []).length, trained: model ? model.size() : 0,
        floodWarnings: (floodPredictions || []).filter((f) => num(f.flood_probability ?? f.floodRiskProbability) !== null && num(f.flood_probability ?? f.floodRiskProbability) >= 0.6).length,
        score, scoreDrivers, band: score === null ? null : score >= 85 ? 'good' : score >= 65 ? 'fair' : score >= 40 ? 'poor' : 'critical',
    };
    const findings = buildTwinFindings({ summary, risks, leaks, anomalies, staleRows, floodPredictions, serverAnomalies, assets });
    return { summary, findings, risks, riskById, leaks, anomalies, stale: staleRows, sensorRows, sensorByKey, liveByEntity, byEntity };
}

export function buildTwinFindings({ summary, risks = [], leaks = [], anomalies = [], staleRows = [], floodPredictions = [], serverAnomalies = [], assets = [] } = {}) {
    const f = [];
    leaks.forEach((l) => {
        const c = l.pipeline?.path_coordinates?.[0];
        f.push({ id: `leak-${l.pipelineId}`, level: l.confidence === 'low' ? 'warning' : 'critical', kind: 'leak', ml: true,
            title: `Suspected leak / burst — ${l.pipeline?.name || `pipeline ${l.pipelineId}`} (${l.confidence} confidence)`, detail: l.text,
            action: { type: 'pipeline', id: l.pipelineId, sensor: l.lead?.key, ...(c ? { lat: num(c.lat ?? c[1]), lng: num(c.lng ?? c[0]) } : {}) } });
    });
    (floodPredictions || []).forEach((p) => {
        const prob = num(p.flood_probability ?? p.floodRiskProbability);
        if (prob === null || prob < 0.6) return;
        const asset = (assets || []).find((a) => String(a.id) === String(p.well_id ?? p.wellId));
        const c = coords(asset);
        f.push({ id: `flood-${p.well_id ?? p.wellId}`, level: prob >= 0.85 ? 'critical' : 'warning', kind: 'flood', ml: true,
            title: `Flood risk at ${p.wellName || `well ${p.well_id ?? p.wellId}`} — ${pct(prob)} (server ML)`,
            detail: `Severity ${p.predictedSeverity || p.flood_risk || '—'}${num(p.estimatedMinutesToOvertopping) !== null ? `, about ${d(p.estimatedMinutesToOvertopping, 0)} min to overtopping` : ''}${p.model_version ? ` · model ${p.model_version}` : ''}.`,
            action: asset ? { type: 'asset', id: asset.id, ...(c || {}) } : null });
    });
    (serverAnomalies || []).slice(0, 5).forEach((a, i) => {
        const key = a.device_id ?? a.deviceId ?? a.sensor_id;
        f.push({ id: `srv-anom-${key ?? i}-${a.timestamp ?? i}`, level: 'critical', kind: 'server_anomaly', ml: true, title: `Critical anomaly reported by the server${key ? ` — ${key}` : ''}`,
            detail: a.message || a.detail || (num(a.psi ?? a.value) !== null ? `Reading ${d(a.psi ?? a.value)}.` : 'See Active Alerts.'), action: key ? { type: 'sensor', id: String(key) } : null });
    });
    risks.filter((r) => r.band === 'critical' || r.band === 'elevated').slice(0, 6).forEach((r) => {
        f.push({ id: `risk-${r.id}`, level: r.band === 'critical' ? 'critical' : 'warning', kind: 'risk', ml: r.drivers.some((x) => /ML/.test(x.label)),
            title: `${r.name} — risk ${r.score}/100 (${r.label})`, detail: `${r.drivers.slice(0, 3).map((x) => x.label).join('; ')}. ${r.actions[0] || ''}`.trim(),
            action: { type: 'asset', id: r.id, ...(r.coords || {}) } });
    });
    anomalies.filter((a) => !leaks.some((l) => l.sensors.includes(a.key))).slice(0, 6).forEach((a) => {
        f.push({ id: `anom-${a.key}`, level: a.level === 'critical' ? 'critical' : 'warning', kind: 'anomaly', ml: true,
            title: `${a.kind === 'drop' ? 'Sudden drop' : 'Spike'} on ${sensorName(a.sensor)}`,
            detail: `${d(a.value)} vs normal ${d(a.mean)} (z = ${d(a.z, 1)}). ${a.kind === 'drop' ? 'Check for a leak, a closed valve or a pump trip.' : 'Check for a surge, a closed downstream valve or a faulty sensor.'}`,
            action: { type: 'sensor', id: a.key, ...(coords(a.sensor) || {}) } });
    });
    if (staleRows.length) {
        f.push({ id: 'stale', level: 'info', kind: 'stale', title: `${staleRows.length} sensor(s) stopped reporting`,
            detail: `${staleRows.slice(0, 6).map((x) => x.key).join(', ')}${staleRows.length > 6 ? '…' : ''} — no reading for over ${Math.round(TWIN_AI_DEFAULTS.staleMs / 60000)} min. Check power / SIM / gateway.`,
            action: { type: 'sensor', id: staleRows[0].key, ...(coords(staleRows[0].sensor) || {}) } });
    }
    if (summary && summary.sensors && summary.noData === summary.sensors && summary.isLive) {
        f.push({ id: 'no-telemetry', level: 'info', kind: 'telemetry', title: 'No telemetry received yet', detail: 'None of the sensors has a reading. The ML models start scoring after 8 readings per sensor.', action: null });
    }
    const rank = { critical: 3, warning: 2, info: 1 };
    return f.sort((a, b) => rank[b.level] - rank[a.level]);
}

// ─── AI explainer ────────────────────────────────────────────────────────────
export const TWIN_QUESTIONS = Object.freeze([
    'What needs attention right now?',
    'Are there any leaks or bursts?',
    'Which sensors are not reporting?',
    'Which asset is most at risk?',
    'How is the network health score calculated?',
]);

export function explainTwinScreen(intel) {
    const s = intel?.summary;
    return [
        'Digital Twin — how to read this screen:',
        '• The 3D map is a live copy of the water network: assets (wells, pumps, tanks), pipelines and IoT sensors. Colours come from live sensor readings and the predictive-maintenance ML model — red glow = critical, orange = warning; magenta-red = critical in a simulation.',
        '• LIVE TELEMETRY means readings stream in over the WebSocket. Drag the timeline (shown when the bottom overview is minimised) to replay the network at an earlier time; "Sync to Live" returns.',
        '• Brain button (AI predictive view): healthy assets fade so only the at-risk ones stand out. Cones mark assets the ML model rates warning or critical.',
        '• Sidebar: Inspect (selected asset, its sensors, ML health, activity), Layers (show / hide, presets), Tools (simulations, AR) and AI (findings, risk ranking, Ask FlowSense).',
        '• FlowSense ML here: every sensor stream learns its own normal level (EWMA) and flags spikes, drops and stuck sensors; pressure drops on the same pipeline are grouped as suspected leaks; each asset gets an explainable risk score.',
        s ? `Right now: ${s.reporting} of ${s.sensors} sensors reporting, ${s.anomalies} anomaly(ies), ${s.leaks} suspected leak(s), ${s.highRisk} asset(s) at critical risk. Network health ${s.score === null ? '—' : `${s.score}/100`}.` : 'Live numbers appear once data has loaded.',
    ].join('\n');
}

export function explainTwinForMinistry(intel) {
    const s = intel?.summary;
    return [
        'FlowSense Digital Twin — how it serves the Ministry of Works / Water & Sewerage:',
        '1. One live picture of the network — every asset, pipeline and sensor on one 3D map, updated as readings arrive, so supervisors see problems instead of waiting for calls.',
        '2. Earlier warning — machine learning learns each sensor\'s normal pressure or level and flags sudden drops (possible leaks or bursts), spikes and sensors that stop reporting; the predictive-maintenance model estimates failure probability and remaining useful life for assets.',
        '3. Less water lost, fewer outages — suspected leaks are pinpointed to a pipeline with the sensors that show it, so crews go to the right place first.',
        '4. Spend where it matters — assets are ranked by an explainable risk score (each point traced to a reason), supporting maintenance planning and budget requests with evidence.',
        '5. Preparedness — the simulation tools test pipe bursts, pump failures and storms on the twin before they happen; the timeline replays past events for reviews and audits.',
        s ? `Right now: ${s.assets} assets, ${s.pipelines} pipelines, ${s.reporting}/${s.sensors} sensors reporting; ${s.leaks} suspected leak(s), ${s.anomalies} anomaly(ies), ${s.highRisk} asset(s) at critical risk, ${s.openAlerts} open alert(s). Network health ${s.score === null ? '—' : `${s.score}/100`}.` : 'Live numbers appear once data has loaded.',
    ].join('\n');
}

export function explainAsset(risk, extra = {}) {
    if (!risk) return 'No live data, ML health score, alerts or issues for this asset yet — nothing to score.';
    const lines = [`${risk.name}: risk ${risk.score}/100 (${risk.label}).`];
    if (risk.drivers.length) lines.push(`Why: ${risk.drivers.map((x) => `${x.label} (+${x.points})`).join('; ')}.`);
    if (extra.health?.predicted_rul_days !== undefined && extra.health?.predicted_rul_days !== null) lines.push(`ML remaining useful life: ${d(extra.health.predicted_rul_days)} days.`);
    lines.push(`Recommended: ${risk.actions.join(' ')}`);
    return lines.join('\n');
}

export function explainSensor(sensor, row, extra = {}) {
    if (!sensor) return '';
    const lines = [`${sensorName(sensor)} (${sensorKey(sensor)}):`];
    if (!row || !row.hasReading) { lines.push('No reading received yet.'); return lines.join('\n'); }
    lines.push(`Current ${formatReading(sensor, row.reading)} — ${row.severity}: ${row.reason}.`);
    const ml = row.ml;
    if (ml && ml.samples >= TWIN_AI_DEFAULTS.minSamples) lines.push(`Learned normal ${d(ml.mean, 1)} ± ${d(ml.std, 1)} from ${ml.samples} readings${ml.z !== null ? `; this reading is ${d(ml.z, 1)} standard deviations from normal` : ''}.`);
    else lines.push(`The ML model is still learning this sensor (${ml ? ml.samples : 0} of ${TWIN_AI_DEFAULTS.minSamples} readings).`);
    if (ml && num(ml.rate) !== null) lines.push(`Rate of change ${d(ml.rate, 2)} per minute.`);
    if (row.anomaly) lines.push(`Anomaly: ${row.anomaly.kind === 'drop' ? 'sudden drop — possible leak, burst, closed valve or pump trip' : 'spike — possible surge or faulty sensor'} (${row.anomaly.level}).`);
    if (row.flat) lines.push('Same value for a long time — the sensor may be stuck.');
    if (row.stale) lines.push('No reading for over 15 minutes — check power and communications.');
    if (extra.condition) lines.push(`PSI rule: "${extra.condition.status_label}" (${extra.condition.min_psi}–${extra.condition.max_psi} PSI).`);
    return lines.join('\n');
}

const has = (q, ...w) => w.some((x) => q.includes(x));
export function answerTwinLocally(question, intel, selected = null) {
    const q = String(question || '').toLowerCase();
    if (!intel?.summary) return { answer: 'The twin is still loading data — try again in a moment.', intent: 'loading' };
    const s = intel.summary;
    const asset = (intel.risks || []).find((r) => r.name && q.includes(String(r.name).toLowerCase()));
    if (asset) return { answer: explainAsset(asset), intent: 'asset' };
    const sens = (intel.sensorRows || []).find((x) => x.key && q.includes(x.key.toLowerCase()));
    if (sens) return { answer: explainSensor(sens.sensor, sens), intent: 'sensor' };
    if (has(q, 'leak', 'burst', 'loss')) {
        if (!intel.leaks.length) return { answer: `No suspected leaks. ${s.reporting} of ${s.sensors} sensors are reporting; leak detection needs pressure sensors linked to pipelines and ${TWIN_AI_DEFAULTS.minSamples}+ readings per sensor.`, intent: 'leak' };
        return { answer: intel.leaks.map((l) => `• ${l.pipeline?.name || `Pipeline ${l.pipelineId}`} (${l.confidence} confidence): ${l.text}`).join('\n'), intent: 'leak' };
    }
    if (has(q, 'not report', 'offline', 'stale', 'silent', 'no data', 'stopped')) {
        const none = (intel.sensorRows || []).filter((x) => !x.hasReading).map((x) => x.key);
        return { answer: `${s.stale} stale (no reading for 15+ min)${intel.stale.length ? `: ${intel.stale.slice(0, 10).map((x) => x.key).join(', ')}` : ''}. ${none.length} never reported${none.length ? `: ${none.slice(0, 10).join(', ')}${none.length > 10 ? '…' : ''}` : ''}.`, intent: 'stale' };
    }
    if (has(q, 'score', 'calculat', 'health')) {
        return { answer: s.score === null ? 'No assets or sensors loaded, so there is no score yet.' : `Network health ${s.score}/100. Starts at 100; deductions: ${s.scoreDrivers.length ? s.scoreDrivers.map((x) => `${x.label} (${x.points})`).join('; ') : 'none'}.`, intent: 'score' };
    }
    if (has(q, 'most at risk', 'risk', 'worst', 'fail', 'rul')) {
        const top = (intel.risks || []).slice(0, 5);
        return { answer: top.length ? top.map((r, i) => `${i + 1}. ${r.name} — ${r.score}/100 (${r.label}): ${r.drivers.slice(0, 2).map((x) => x.label).join('; ') || 'no drivers'}`).join('\n') : 'No asset has enough data to score yet (no ML health scores, live readings, alerts or issues).', intent: 'risk' };
    }
    if (has(q, 'anomal', 'spike', 'drop', 'unusual')) {
        return { answer: intel.anomalies.length ? intel.anomalies.slice(0, 8).map((a) => `• ${a.key}: ${a.kind} — ${d(a.value)} vs normal ${d(a.mean)} (z ${d(a.z, 1)})`).join('\n') : `No anomalies. The ML model has learned ${s.trained} sensor stream(s).`, intent: 'anomaly' };
    }
    if (has(q, 'alert', 'issue')) return { answer: `${s.openAlerts} open alert(s) of ${s.alerts}, ${s.issues} reported issue(s). Use the Layers tab to show them on the map.`, intent: 'alerts' };
    if (has(q, 'selected', 'this asset', 'this sensor') && selected) {
        const r = intel.riskById?.get(String(selected.id));
        return { answer: r ? explainAsset(r) : 'The selected object has no scored data yet.', intent: 'selected' };
    }
    if (has(q, 'attention', 'now', 'priorit', 'summary', 'status')) {
        const top = (intel.findings || []).slice(0, 5);
        return { answer: top.length ? top.map((f) => `• [${f.level}] ${f.title}`).join('\n') : `Nothing needs attention. ${s.reporting}/${s.sensors} sensors reporting, network health ${s.score === null ? '—' : `${s.score}/100`}.`, intent: 'attention' };
    }
    return { answer: `I can answer about leaks, anomalies, stale sensors, asset risk, alerts and the health score. Now: ${s.reporting}/${s.sensors} sensors reporting, ${s.anomalies} anomaly(ies), ${s.leaks} suspected leak(s), health ${s.score === null ? '—' : `${s.score}/100`}.`, intent: 'help' };
}

/** Compact, number-only context for the FlowSense AI engine (no raw readings dump). */
export function buildTwinContext(intel, { selected = null, sensor = null, isLive = true, historicalTimestamp = null, simulation = null } = {}) {
    if (!intel?.summary) return { module: 'digital_twin', loading: true };
    const s = intel.summary;
    const ctx = {
        module: 'digital_twin', organisation: 'Ministry of Works / Water & Sewerage, The Bahamas', mode: isLive ? 'live' : `historical ${historicalTimestamp ? new Date(historicalTimestamp).toISOString() : ''}`,
        summary: { assets: s.assets, pipelines: s.pipelines, sensors: s.sensors, reporting: s.reporting, stale: s.stale, anomalies: s.anomalies, suspected_leaks: s.leaks, assets_ml_critical: s.healthCritical, assets_ml_warning: s.healthWarning, open_alerts: s.openAlerts, issues: s.issues, network_health: s.score, health_deductions: s.scoreDrivers },
        findings: (intel.findings || []).slice(0, 8).map((f) => ({ level: f.level, title: f.title, detail: f.detail })),
        top_risks: (intel.risks || []).slice(0, 8).map((r) => ({ asset: r.name, id: r.id, score: r.score, band: r.band, drivers: r.drivers.map((x) => x.label), actions: r.actions })),
        rules: 'Use only these numbers. Unknown values are null. Do not invent readings, costs or dates.',
    };
    if (selected) { const r = intel.riskById?.get(String(selected.id)); ctx.selected_asset = { id: selected.id, name: selected.name, type: selected.asset_type || selected.type, status: selected.status, risk: r ? { score: r.score, drivers: r.drivers.map((x) => x.label), actions: r.actions } : null }; }
    if (sensor) { const row = intel.sensorByKey?.get(sensorKey(sensor)); ctx.selected_sensor = { id: sensorKey(sensor), type: sensor.type, reading: row?.reading ? readingValue(row.reading) : null, severity: row?.severity, reason: row?.reason, ml: row?.ml ? { mean: row.ml.mean, std: row.ml.std, z: row.ml.z, samples: row.ml.samples, kind: row.ml.kind } : null, stale: row?.stale }; }
    if (simulation) ctx.simulation = simulation;
    return ctx;
}

/** Sorted (oldest → newest) numeric series from mixed history arrays (REST or WS, any order). */
export function seriesOf(history = []) {
    return (history || []).map((p) => ({ t: readingTime(p) ?? num(p?.t), v: p && typeof p === 'object' && 'v' in p ? num(p.v) : readingValue(p) }))
        .filter((p) => p.v !== null)
        .sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
}
